import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { MapPin, Calendar, Zap, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { projects as mockProjects } from '../mockData';
import { getProjects } from '../services/api';

export const Projects = () => {
  const [selectedType, setSelectedType] = useState('All');
  const [projects, setProjects] = useState(mockProjects);
  const [loading, setLoading] = useState(true);
  
  const projectTypes = ['All', 'Residential', 'Commercial', 'Industrial'];

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const data = await getProjects();
        if (data.length > 0) setProjects(data);
      } catch (error) {
        console.log('Using mock projects');
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);
  
  const filteredProjects = selectedType === 'All' 
    ? projects 
    : projects.filter(p => p.type === selectedType);

  return (
    <div className="min-h-screen pt-24 pb-16">
      {/* Hero Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-orange-50 via-blue-50 to-white">
        <div className="container mx-auto text-center">
          <h1 className="text-5xl lg:text-6xl font-bold mb-6">
            Our <span className="text-orange-500">Projects</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Explore our portfolio of successful solar installations across Telangana and India. 
            Each project represents our commitment to quality and customer satisfaction.
          </p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 px-4 bg-white border-b border-gray-200">
        <div className="container mx-auto">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center text-gray-700 font-semibold">
              <Filter className="h-5 w-5 mr-2" />
              Filter by:
            </div>
            <div className="flex flex-wrap gap-3">
              {projectTypes.map((type) => (
                <Button
                  key={type}
                  onClick={() => setSelectedType(type)}
                  className={`rounded-lg font-medium transition-all duration-300 ${
                    selectedType === type
                      ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {type}
                </Button>
              ))}
            </div>
            <div className="ml-auto text-sm text-gray-600">
              Showing {filteredProjects.length} projects
            </div>
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project) => (
              <Card key={project.id} className="border-none shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 overflow-hidden group">
                <div className="relative overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4 bg-white px-3 py-1 rounded-full text-xs font-semibold text-gray-700">
                    {project.type}
                  </div>
                  <div className="absolute top-4 right-4 bg-gradient-to-r from-orange-500 to-orange-600 px-4 py-2 rounded-full text-sm font-bold text-white shadow-lg">
                    {project.capacity}
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-3 text-gray-900">{project.title}</h3>
                  <p className="text-gray-600 mb-4 leading-relaxed text-sm">{project.description}</p>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <MapPin className="h-4 w-4 text-orange-500 mr-2" />
                      {project.location}, {project.state}
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Calendar className="h-4 w-4 text-orange-500 mr-2" />
                      Completed: {project.completion_date || project.completionDate}
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Zap className="h-4 w-4 text-orange-500 mr-2" />
                      Savings: {project.savings_per_year || project.savingsPerYear}/year
                    </div>
                  </div>
                  
                  <div className="pt-4 border-t border-gray-200">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">Annual Savings</span>
                      <span className="text-lg font-bold text-orange-600">{project.savings_per_year || project.savingsPerYear}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              Projects <span className="text-orange-500">Impact</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-none shadow-lg text-center">
              <CardContent className="p-8">
                <div className="text-5xl font-bold text-orange-600 mb-2">150+</div>
                <p className="text-gray-600 font-medium">Total Projects</p>
              </CardContent>
            </Card>
            <Card className="border-none shadow-lg text-center">
              <CardContent className="p-8">
                <div className="text-5xl font-bold text-orange-600 mb-2">65+ MW</div>
                <p className="text-gray-600 font-medium">Total Capacity</p>
              </CardContent>
            </Card>
            <Card className="border-none shadow-lg text-center">
              <CardContent className="p-8">
                <div className="text-5xl font-bold text-orange-600 mb-2">₹58Cr+</div>
                <p className="text-gray-600 font-medium">Customer Savings</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl font-bold mb-4">Want to See Your Project Here?</h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Join hundreds of satisfied customers who have switched to solar energy with SolarBull
          </p>
          <Link to="/contact">
            <Button className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-6 text-lg rounded-xl font-semibold shadow-xl">
              Start Your Project
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
